import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import type { AxiosError } from "axios";
import type {
  AdminMfaResetRequestBody,
  AdminMfaResetResponse,
  AuthStatusResponse,
  MfaChallengeStatusResponse,
  MfaCodeRequestBody,
  MfaEnrollConfirmRequestBody,
  MfaEnrollStartRequestBody,
  MfaErrorCode,
  MfaErrorResponse,
  MfaRecoveryCodesResponse,
  MfaSessionResponse,
  MfaSetupResponse,
  MfaStatusResponse,
  MfaStepUpRequestBody,
  MfaVerifyRequestBody,
} from "common";

/**
 * Every MFA call uses the /api/auth/v1 mount: the challenge and device
 * cookies are scoped to /api/auth, which /api/v1/auth is outside of.
 */
export const MFA_BASE = "/api/auth/v1/mfa";
type MfaError = AxiosError<MfaErrorResponse>;

export const mfaErrorMessage = (
  error: unknown
): { message: string; code?: MfaErrorCode; lockedUntil?: string } => {
  const body = (error as MfaError)?.response?.data as
    | (Partial<MfaErrorResponse> & { message?: string })
    | undefined;
  if (body?.error === "locked" && body.lockedUntil) {
    return {
      code: "locked",
      lockedUntil: body.lockedUntil,
      message: `Too many wrong codes. Try again after ${new Date(body.lockedUntil).toLocaleTimeString()}.`,
    };
  }
  if (body?.message) return { message: body.message, code: body.error };
  return { message: "Something went wrong. Please try again." };
};

/** The longest wait between two reads of a locked challenge. */
export const MFA_LOCKED_POLL_MS = 30_000;

/**
 * While the challenge is locked it is read again every 30 seconds, and once more as the lock
 * ends, so the code screen returns without a reload. An unlocked answer stops the polling.
 */
export const lockedPollInterval = (
  lockedUntil: string | null | undefined,
  now = Date.now()
): number | false => {
  if (!lockedUntil) return false;
  const remaining = new Date(lockedUntil).getTime() - now;
  return Math.max(1_000, Math.min(MFA_LOCKED_POLL_MS, remaining));
};

export const useMfaChallenge = () =>
  useQuery<MfaChallengeStatusResponse, MfaError>({
    queryKey: [`${MFA_BASE}/challenge`],
    queryFn: async () => (await axios.get(`${MFA_BASE}/challenge`)).data,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: query => lockedPollInterval(query.state.data?.lockedUntil),
  });

export const useMfaVerify = () =>
  useMutation<MfaSessionResponse, MfaError, MfaVerifyRequestBody>({
    mutationFn: async body =>
      (await axios.post(`${MFA_BASE}/verify`, body)).data,
  });

export const useMfaEnrollStart = () =>
  useMutation<MfaSetupResponse, MfaError, MfaEnrollStartRequestBody>({
    mutationFn: async body =>
      (await axios.post(`${MFA_BASE}/enroll/start`, body)).data,
  });

export const useMfaEnrollConfirm = () =>
  useMutation<MfaSessionResponse, MfaError, MfaEnrollConfirmRequestBody>({
    mutationFn: async body =>
      (await axios.post(`${MFA_BASE}/enroll/confirm`, body)).data,
  });

export const useMfaStatus = () =>
  useQuery<MfaStatusResponse, MfaError>({
    queryKey: [MFA_BASE],
    queryFn: async () => (await axios.get(MFA_BASE)).data,
  });

/**
 * The whole /api/auth/v1/status answer. `useCheckAuthStatus` keeps only `authenticated` under
 * the bare key; this sits under a longer key so every invalidation of the bare key refreshes it.
 * `enabled` false makes no request at all.
 */
export const useAuthStatusDetails = (enabled = true) =>
  useQuery<AuthStatusResponse, MfaError>({
    queryKey: ["/api/auth/v1/status", "details"],
    queryFn: async () => (await axios.get("/api/auth/v1/status")).data,
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  });

const useStatusInvalidatingMutation = <TResult, TBody>(
  run: (body: TBody) => Promise<TResult>
) => {
  const queryClient = useQueryClient();
  return useMutation<TResult, MfaError, TBody>({
    mutationFn: run,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MFA_BASE] }),
  });
};

export const useMfaSetupStart = () =>
  useMutation<MfaSetupResponse, MfaError, MfaStepUpRequestBody>({
    mutationFn: async body =>
      (await axios.post(`${MFA_BASE}/setup/start`, body)).data,
  });

export const useMfaSetupConfirm = () =>
  useStatusInvalidatingMutation<MfaRecoveryCodesResponse, MfaCodeRequestBody>(
    async body => (await axios.post(`${MFA_BASE}/setup/confirm`, body)).data
  );

export const useRegenerateRecoveryCodes = () =>
  useStatusInvalidatingMutation<MfaRecoveryCodesResponse, MfaCodeRequestBody>(
    async body => (await axios.post(`${MFA_BASE}/recovery-codes`, body)).data
  );

export const useAckRecoveryCodes = () =>
  useStatusInvalidatingMutation<unknown, void>(
    async () => (await axios.post(`${MFA_BASE}/recovery-codes/ack`, {})).data
  );

export const useDisableMfa = () =>
  useStatusInvalidatingMutation<unknown, MfaCodeRequestBody>(
    async body => (await axios.delete(MFA_BASE, { data: body })).data
  );

// An empty JSON body keeps the Content-Type header the server requires.
export const useForgetDevices = () =>
  useStatusInvalidatingMutation<unknown, void>(
    async () => (await axios.delete(`${MFA_BASE}/devices`, { data: {} })).data
  );

export const useSignOutEverywhere = () =>
  useMutation<unknown, MfaError, void>({
    mutationFn: async () =>
      (await axios.post("/api/auth/v1/sign-out-everywhere", {})).data,
  });

export const useAdminResetMfa = () =>
  useMutation<
    AdminMfaResetResponse,
    MfaError,
    { userId: number; body: AdminMfaResetRequestBody }
  >({
    mutationFn: async ({ userId, body }) =>
      (await axios.post(`/api/user/v1/users/${userId}/mfa/reset`, body)).data,
  });
